import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI literacy coach evaluating a learner's workflow use case identification work.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

WORKFLOW MAPPING: Are all significant workflow steps captured with honest time estimates? (1-4)

TASK TYPE ACCURACY: Are the four task types (Repetitive, Communication, Research, Synthesis) correctly applied? (1-4)

OUTPUT DESCRIPTION: For each AI-ready step, is there a clear one-sentence description of what AI produces: not the topic, the specific deliverable? (1-4)

DAILY FILTER APPLIED: Has the learner honestly evaluated whether each use case is something they would run every single day? (1-4)

OVERALL USE CASE LIST: Is this list ready to build role-specific prompts from in Day 4? (1-4)

After scoring:
1. Give a total score out of 20.
2. Identify the weakest output description and rewrite it to show what excellent looks like.
3. Name one use case that should be removed (not AI-ready or not significant) and explain why.
4. Name one workflow step the learner may have overlooked and explain why it could be valuable.

Here is my workflow use case list:

[PASTE YOUR WORKFLOW NAME, ALL STEPS, AND USE CASE ENTRIES HERE]`;

const USE_CASE_RUBRIC = [
  {
    criterion: 'WORKFLOW MAPPING',
    missing: 'Steps missing or out of order',
    partial: 'Major steps there, minor gaps',
    solid: 'All significant steps captured',
    excellent: 'Every step including manual/embarrassing ones, honest times',
  },
  {
    criterion: 'TASK TYPE ACCURACY',
    missing: 'Types not applied or all wrong',
    partial: 'Some types correct',
    solid: 'Most types correctly identified',
    excellent: 'All types correctly identified with accurate distinctions',
  },
  {
    criterion: 'OUTPUT DESCRIPTION',
    missing: 'No description or just the task name',
    partial: 'Vague: describes the topic not the output',
    solid: 'Specific output type named clearly',
    excellent: 'Specific output + format + destination in one clear sentence',
  },
  {
    criterion: 'DAILY FILTER APPLIED',
    missing: 'Not considered or all marked yes',
    partial: 'Applied inconsistently',
    solid: 'Applied to most use cases',
    excellent: 'Every use case evaluated with honest reasoning',
  },
  {
    criterion: 'OVERALL USE CASE LIST',
    missing: "Incomplete, wouldn't lead to useful prompts",
    partial: 'Partial: some gaps',
    solid: 'Solid foundation for Day 4 prompts',
    excellent: 'Ready to build role-specific prompts immediately',
  },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay3({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day3'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day3: { ...(existing.progress?.day3 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day3: { ...(existing.progress?.day3 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day3: { ...(existing.progress?.day3 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_mapping', 'score_tasktype', 'score_output', 'score_daily', 'score_overall']);

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 3 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={3} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 3</div>
        <div style={s.headerTitle}>Use Case Identification</div>
        <div style={s.headerDesc}>Map your workflow. Find where AI actually belongs.</div>
      </div>

      {/* Key Concept Reminder */}
      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Not every task belongs in AI, and trying to use it on everything is how people burn out on it. The goal is strategic use: finding the steps in your actual workflows where AI consistently saves time and produces useful output. Every AI-ready task falls into at least one of four types. Repetitive: same structure every time, different content. Communication: something you write and send. Research: find or summarize information before you can act. Synthesis: pull from multiple sources into one coherent output. For each AI-ready step, you need to be able to name what AI produces: one clear sentence describing the output. That output description is the foundation every prompt you build from here forward is built on.
        </div>
      </div>

      {/* Use Case Finder */}
      <CalloutBox title="RUN THE USE CASE FINDER FIRST">
        Before you map your workflow, open the AI Use Case Finder in the portal and run it. Answer the few questions about your role, your systems, and what eats up your week. The more detail you give it, the sharper your results, the same way a richer RICE prompt gives you a better output. It hands back a personalized set of use cases split into quick wins you can try in a chat window today and bigger plays worth building an agent for. Bring those into the mapping you're about to do, so you start with real candidates instead of a blank page. Hold onto anything it flags as worth building an agent for, because that's your head start in Day 6.
      </CalloutBox>

      {/* In-Session Exercise */}
      <Section title="In-Session Exercise — Map Your Workflow and Find Your Use Cases">
        <div style={s.exerciseIntro}>
          Pick one real workflow from your job: a recurring sequence of tasks you complete to get a specific result. Not a one-time project. Not a single task. Something you do regularly with a beginning, a middle, and an end.
        </div>

        {/* Step 1 */}
        <div style={s.partHeader}>STEP 1 — NAME YOUR WORKFLOW</div>
        <Field label="My workflow:" value={data.workflow_name} onChange={v => set('workflow_name', v)} minHeight={44} />
        <div style={s.field}>
          <div style={s.fieldLabel}>How often do I run this?</div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', margin: '8px 0', alignItems: 'center' }}>
            {['Daily', 'Weekly', 'Monthly'].map(opt => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="radio" name="frequency" checked={data.frequency === opt} onChange={() => set('frequency', opt)} /> {opt}
              </label>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="radio" name="frequency" checked={data.frequency === 'Other'} onChange={() => set('frequency', 'Other')} /> Other:
            </label>
            {data.frequency === 'Other' && (
              <input
                type="text"
                style={{ border: '1.5px solid #E8E6E2', padding: '4px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', width: '160px' }}
                value={data.frequency_other || ''}
                onChange={e => set('frequency_other', e.target.value)}
              />
            )}
          </div>
        </div>

        {/* Step 2 */}
        <div style={s.partHeader}>STEP 2 — MAP EVERY STEP</div>
        <div style={s.partIntro}>
          Write down every step in the order it actually happens. Include the ones that feel embarrassingly manual. Be honest about the time each step takes.
        </div>

        <div style={s.workflowTable}>
          <div style={s.workflowHeader}>
            <div style={{ ...s.workflowHCol, width: '36px', flexShrink: 0 }}>#</div>
            <div style={{ ...s.workflowHCol, flex: 3 }}>STEP (what you actually do)</div>
            <div style={{ ...s.workflowHCol, flex: 1 }}>TIME IT TAKES</div>
            <div style={{ ...s.workflowHCol, flex: 1 }}>TASK TYPE (R / C / S / Sy)</div>
            <div style={{ ...s.workflowHCol, flex: 1, borderRight: 'none' }}>AI-READY? (Y / N / Maybe)</div>
          </div>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
            <div key={n} style={s.workflowRow}>
              <div style={s.workflowNumCell}>{n}</div>
              <div style={{ ...s.workflowCell, flex: 3 }}>
                <textarea style={s.tableInput} value={data[`step_${n}_desc`] || ''} onChange={e => set(`step_${n}_desc`, e.target.value)} />
              </div>
              <div style={{ ...s.workflowCell, flex: 1 }}>
                <textarea style={s.tableInput} value={data[`step_${n}_time`] || ''} onChange={e => set(`step_${n}_time`, e.target.value)} />
              </div>
              <div style={{ ...s.workflowCell, flex: 1 }}>
                <textarea style={s.tableInput} value={data[`step_${n}_type`] || ''} onChange={e => set(`step_${n}_type`, e.target.value)} />
              </div>
              <div style={{ ...s.workflowCell, flex: 1, borderRight: 'none' }}>
                <textarea style={s.tableInput} value={data[`step_${n}_aiready`] || ''} onChange={e => set(`step_${n}_aiready`, e.target.value)} />
              </div>
            </div>
          ))}
          <div style={{ ...s.workflowRow, borderBottom: 'none' }}>
            <div style={{ ...s.workflowNumCell, background: '#F2F0EC' }}> </div>
            <div style={{ ...s.workflowCell, flex: 3, background: '#F2F0EC', padding: '10px 12px', fontSize: '11px', fontWeight: '700', color: '#1B2A4A', display: 'flex', alignItems: 'center' }}>TOTAL TIME</div>
            <div style={{ ...s.workflowCell, flex: 1 }}>
              <textarea style={s.tableInput} value={data.total_time || ''} onChange={e => set('total_time', e.target.value)} />
            </div>
            <div style={{ ...s.workflowCell, flex: 1, background: '#F2F0EC' }}> </div>
            <div style={{ ...s.workflowCell, flex: 1, background: '#F2F0EC', borderRight: 'none' }}> </div>
          </div>
        </div>
        <div style={{ fontSize: '11px', color: '#3D3D3D', marginTop: '8px', fontStyle: 'italic', marginBottom: '20px' }}>
          Task Type Key: R = Repetitive &nbsp;&nbsp; C = Communication &nbsp;&nbsp; S = Research &nbsp;&nbsp; Sy = Synthesis
        </div>

        {/* Step 3 */}
        <div style={s.partHeader}>STEP 3 — BUILD YOUR USE CASE LIST</div>
        <div style={s.partIntro}>
          For every step you marked AI-Ready, complete the entry below. The output description is the most important field: one clear sentence describing exactly what AI produces for this step. This sentence becomes the Intent component of your RICE prompt in Day 4.
        </div>

        {[1, 2, 3, 4].map(n => (
          <div key={n} style={s.useCaseBlock}>
            <div style={s.useCaseTitle}>USE CASE {n}</div>
            <div style={s.useCaseRow}>
              <div style={s.useCaseLabel}>
                <div style={s.useCaseLabelName}>STEP NAME</div>
                <div style={s.useCaseLabelHint}>Which step in the workflow?</div>
              </div>
              <textarea style={{ ...s.textarea, flex: 1, border: 'none', minHeight: 50, background: '#FAF8F5' }} value={data[`uc_${n}_step`] || ''} onChange={e => set(`uc_${n}_step`, e.target.value)} />
            </div>
            <div style={s.useCaseRow}>
              <div style={s.useCaseLabel}>
                <div style={s.useCaseLabelName}>TASK TYPE</div>
                <div style={s.useCaseLabelHint}>Repetitive / Communication / Research / Synthesis (circle)</div>
              </div>
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '12px', background: '#FAF8F5', alignItems: 'center' }}>
                {['Repetitive', 'Communication', 'Research', 'Synthesis'].map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#3D3D3D' }}>
                    <input type="radio" name={`uc_${n}_type`} checked={data[`uc_${n}_type`] === t} onChange={() => set(`uc_${n}_type`, t)} /> {t}
                  </label>
                ))}
              </div>
            </div>
            <div style={s.useCaseRow}>
              <div style={s.useCaseLabel}>
                <div style={s.useCaseLabelName}>OUTPUT DESCRIPTION</div>
                <div style={s.useCaseLabelHint}>One sentence: what does AI produce here? What does done look like?</div>
              </div>
              <textarea style={{ ...s.textarea, flex: 1, border: 'none', minHeight: 64, background: '#FAF8F5' }} value={data[`uc_${n}_output`] || ''} onChange={e => set(`uc_${n}_output`, e.target.value)} />
            </div>
            <div style={{ ...s.useCaseRow, borderBottom: 'none' }}>
              <div style={s.useCaseLabel}>
                <div style={s.useCaseLabelName}>WOULD I RUN THIS DAILY?</div>
                <div style={s.useCaseLabelHint}>Yes / No / Sometimes, and why?</div>
              </div>
              <div style={{ flex: 1, padding: '12px', background: '#FAF8F5' }}>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  {['Yes', 'No', 'Sometimes'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#3D3D3D' }}>
                      <input type="radio" name={`uc_${n}_daily`} checked={data[`uc_${n}_daily`] === opt} onChange={() => set(`uc_${n}_daily`, opt)} /> {opt}
                    </label>
                  ))}
                </div>
                <textarea
                  style={{ ...s.textarea, minHeight: 44, background: '#fff' }}
                  placeholder="...and why?"
                  value={data[`uc_${n}_daily_why`] || ''}
                  onChange={e => set(`uc_${n}_daily_why`, e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* Scoring Criteria */}
      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate the quality of your use case list.
        </div>
        <RubricTable rows={USE_CASE_RUBRIC} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your use case list:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Mapping" value={data.score_mapping} onChange={v => set('score_mapping', v)} />
          <ScoreField label="Task Type" value={data.score_tasktype} onChange={v => set('score_tasktype', v)} />
          <ScoreField label="Output" value={data.score_output} onChange={v => set('score_output', v)} />
          <ScoreField label="Daily Filter" value={data.score_daily} onChange={v => set('score_daily', v)} />
          <ScoreField label="Overall" value={data.score_overall} onChange={v => set('score_overall', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      {/* AI Scoring Prompt — always visible, no toggle */}
      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this prompt into your AI tool along with your complete use case list. The more detail you provide, the more specific and useful the feedback will be.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      {/* Scores from AI Evaluation */}
      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={44} />
        <Field label="Weakest output description the AI rewrote: what changed?" value={data.ai_weakest} onChange={v => set('ai_weakest', v)} minHeight={70} />
        <Field label="Use case the AI said to remove: do you agree? Why?" value={data.ai_remove} onChange={v => set('ai_remove', v)} minHeight={70} />
        <Field label="Overlooked workflow step the AI identified: is it valid?" value={data.ai_overlooked} onChange={v => set('ai_overlooked', v)} minHeight={70} />
      </Section>

      {/* The Standard */}
      <CalloutBox title="THE STANDARD: 16 / 20">
        Keep working until your use case list scores 16/20 or higher AND every output description is specific enough that someone could build a RICE prompt from it immediately. Use the revision workspace below.
      </CalloutBox>

      {/* Revision Workspace */}
      <Section title="Revision Workspace — Keep Refining Until You Hit the Standard">
        <div style={s.revisionLabel}>REVISION 1</div>
        <Field label="What I changed and why:" value={data.rev1_what} onChange={v => set('rev1_what', v)} />
        <Field label="Revised use case list or output description:" value={data.rev1_list} onChange={v => set('rev1_list', v)} minHeight={80} />
        <Field label="New AI Score: ___ / 20" value={data.rev1_score} onChange={v => set('rev1_score', v)} minHeight={44} />
        <div style={{ ...s.revisionLabel, marginTop: '24px' }}>REVISION 2 (if needed)</div>
        <Field label="What I changed and why:" value={data.rev2_what} onChange={v => set('rev2_what', v)} />
        <Field label="Revised use case list or output description:" value={data.rev2_list} onChange={v => set('rev2_list', v)} minHeight={80} />
        <Field label="New AI Score: ___ / 20" value={data.rev2_score} onChange={v => set('rev2_score', v)} minHeight={44} />
      </Section>

      {/* Reflection */}
      <Section title="Reflection">
        <Field
          label="1. Look at the steps you marked as NOT AI-ready. What is it about those steps that keeps them human? What would have to change for AI to be useful there?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. Which use case surprised you most, either because you expected it to be AI-ready and it wasn't, or because you didn't think of it until you mapped the workflow?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      {/* Wins Tracker */}
      <Section title="Wins Tracker — Day 3">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      {/* Before Day 4 Assignment */}
      <CalloutBox title="BEFORE DAY 4 — YOUR ASSIGNMENT">
        <span>Finalize your use case list from today. Then:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Make sure you have at least 3 AI-ready use cases. More is fine, 3 is the floor.</li>
          <li>For each use case, write a clean, final output description. One sentence. Specific enough that someone could build a RICE prompt from it right now.</li>
          <li>Mark each use case with your honest daily filter answer: Yes, Sometimes, or No.</li>
          <li>Remove anything you marked No. Those aren't library candidates.</li>
          <li>Come to Day 4 knowing your top use case: the one you would run every single day without question. That's the first prompt you build in Day 4.</li>
        </ol>
      </CalloutBox>
      <Field label="My top use case for Day 4:" value={data.top_use_case} onChange={v => set('top_use_case', v)} minHeight={60} />

      {/* Bottom bar */}
      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 3 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 3 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 3 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '20px' },
  partHeader: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', background: '#F2F0EC', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #D4A847' },
  partIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '16px' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  workflowTable: { border: '1.5px solid #E8E6E2', marginBottom: '8px' },
  workflowHeader: { display: 'flex', background: '#1B2A4A' },
  workflowHCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  workflowRow: { display: 'flex', borderBottom: '1px solid #E8E6E2' },
  workflowNumCell: { width: '36px', flexShrink: 0, padding: '10px 0', fontSize: '12px', fontWeight: '700', color: '#1B2A4A', background: '#F2F0EC', borderRight: '1px solid #E8E6E2', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14px' },
  workflowCell: { borderRight: '1px solid #E8E6E2' },
  tableInput: { width: '100%', border: 'none', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '54px', lineHeight: '1.5', boxSizing: 'border-box', display: 'block' },
  useCaseBlock: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  useCaseTitle: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  useCaseRow: { display: 'flex', borderBottom: '1px solid #E8E6E2' },
  useCaseLabel: { width: '160px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  useCaseLabelName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  useCaseLabelHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '160px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '160px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  revisionLabel: { fontSize: '12px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
